import { SavedAirResponseDataDTO } from "../api/response.types";
import { AirAirlineNameModel, AirDirectionModel, AirReferenceModel } from "./types";
import { AirStatusesModel } from "entities/shared/airCard.types";
import { CoordinateModel, DateTimeModel, ShipmentVariantModel } from "entities/shared/shipmentCard.types";
import { calcProgress } from "entities/shared/utils";
import { BaseModel } from "shared/api/lib/baseModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";

import { SavedAirResponseParser } from "../api/parser";

export const isAirSavedCardModel = (model: Nullable<{ responseType: string }>): model is AirSavedCardModel =>
  model?.responseType === ShipmentVariantModel.AirSaved;

export class AirSavedCardModel extends BaseModel<SavedAirResponseParser, SavedAirResponseDataDTO> {
  public readonly responseType: ShipmentVariantModel;

  constructor(DTO: SavedAirResponseDataDTO) {
    super({ apiParser: new SavedAirResponseParser(DTO), DTO });

    this.responseType = ShipmentVariantModel.AirSaved;
  }

  get id(): number {
    return this.apiParser.id;
  }

  get pieces(): string {
    return this.apiParser.pieces;
  }

  get airlineName(): string {
    return this.apiParser.airlineName;
  }

  get reference(): AirReferenceModel | null {
    return this.apiParser.reference;
  }

  get directionFrom(): string | null {
    return this.apiParser.fromDirection;
  }

  get directionTo(): string | null {
    return this.apiParser.toDirection;
  }

  get portNames(): { to: string | null; from: string | null } {
    return this.apiParser.portNames;
  }

  get status(): ValueOf<typeof AirStatusesModel> {
    return this.apiParser.status;
  }

  get carrierName(): AirAirlineNameModel | null {
    return this.apiParser.carrierName;
  }

  get carrierScac(): AirAirlineNameModel | null {
    return this.apiParser.carrierScac;
  }

  get podCoordinate(): CoordinateModel | null {
    return this.apiParser.podCoordinate;
  }

  get atd(): DateTimeModel | null {
    return this.apiParser.atd;
  }

  get etd(): DateTimeModel | null {
    return this.apiParser.etd;
  }

  get eta(): DateTimeModel | null {
    return this.apiParser.eta;
  }

  get ata(): DateTimeModel | null {
    return this.apiParser.ata;
  }

  get updatedAt(): string {
    return this.apiParser.updatedAt;
  }

  get currentPin(): CoordinateModel | null {
    return this.apiParser.currentPin;
  }

  get tags(): string[] {
    return this.apiParser.tags;
  }

  get progress(): number {
    return calcProgress({
      startDate: this.atd ?? this.etd,
      endDate: this.ata ?? this.eta,
      airStatus: this.status,
    });
  }

  public getLocationName(location: Nullable<AirDirectionModel>): string | null {
    if (!location) return null;
    if (location.nearestCity) {
      return `${location.iataCode} (${location.nearestCity})`;
    }
    return location?.iataCode;
  }
}
